import { Component, inject } from "@angular/core";
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { NgbModalConfig } from "@ng-bootstrap/ng-bootstrap";
import { NgxLoadingBar } from "@ngx-loading-bar/core";
import { filter } from "rxjs";
import { environment } from "../environments/environment";
import { AnalyticsService } from "./core/services/analytics.service";
import { UpdateNotificationComponent } from "./core/ui/update-notification/update-notification.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [RouterOutlet, NgxLoadingBar, UpdateNotificationComponent],
  standalone: true,
})
export class AppComponent {
  router = inject(Router);
  modalConfig = inject(NgbModalConfig);
  analyticsService = inject(AnalyticsService);

  constructor() {
    this.modalConfig.container = "#main";
    this.modalConfig.windowClass = "main-modal";
    this.modalConfig.keyboard = true;

    // Subscribe to router events for both logging and analytics.
    // The Jax `?jax=` query-param handler lives in MainLayoutComponent
    // (app-mode only) — public org pages don't have a Jax assistant outlet
    // and shouldn't pay for the assistant/help service graph.
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      // Development logging
      if (!environment.PRODUCTION) {
        console.log("Navigation ended:", { id: event.id, url: event.url });
      }

      // Analytics tracking for all successful navigations
      this.trackPageView(event.url);
    });

    // Keep error logging for development
    if (!environment.PRODUCTION) {
      this.router.events.subscribe((event: Event) => {
        if (event instanceof NavigationStart) {
          console.log("Navigation started:", { id: event.id, url: event.url });
        }
        if (event instanceof NavigationEnd) {
          console.log("Navigation ended:", { id: event.id, url: event.url });
        }
        if (event instanceof NavigationCancel) {
          console.log("Navigation cancelled:", {
            id: event.id,
            url: event.url,
            reason: event.reason,
          });
        }
        if (event instanceof NavigationError) {
          console.error("Navigation error:", {
            id: event.id,
            url: event.url,
            error: event.error,
          });
        }
      });
    }
  }

  private trackPageView(url: string) {
    // Parse the URL to extract meaningful page information
    const urlSegments = url.split("/").filter((segment) => segment.length > 0);

    if (urlSegments.length === 0) {
      // Root/dashboard page
      this.analyticsService.capture("page_viewed", {
        page: "dashboard",
        url: url,
      });
      return;
    }

    const primaryRoute = urlSegments[0];
    let pageData: Record<string, any> = { page: primaryRoute, url: url };

    // Add specific tracking for different sections
    switch (primaryRoute) {
      case "planner":
        pageData = {
          ...pageData,
          feature: "lesson_planning",
        };
        break;
      case "standards":
        pageData = {
          ...pageData,
          feature: "standards_management",
        };
        if (urlSegments.length > 1) {
          pageData = {
            ...pageData,
            standard_level_id: urlSegments[1],
            action: "view_standard_details",
          };
        }
        break;
      case "team":
        pageData = {
          ...pageData,
          feature: "team_collaboration",
        };
        break;
      case "learn":
        pageData = {
          ...pageData,
          feature: "shared_planners",
        };
        if (urlSegments.length > 1) {
          pageData = {
            ...pageData,
            planner_link_id: urlSegments[1],
          };
        }
        break;
      case "bible":
        pageData = {
          ...pageData,
          feature: "bible_study",
        };
        break;
      case "documents":
        pageData = {
          ...pageData,
          feature: "document_management",
        };
        break;
      case "profile":
      case "subscribe":
      case "payment":
        pageData = {
          ...pageData,
          feature: "account_management",
        };
        break;
    }

    this.analyticsService.capture("page_viewed", pageData);
  }

}
